import { Box, LoadingOverlay } from "@mantine/core";
import { useDebouncedCallback, useDisclosure } from "@mantine/hooks";
import { Editor } from "@tinymce/tinymce-react";
import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { uploadFile } from "../../api/ApiProduct";
import { convertImageDomain } from "../../const/converhandle";
import { useProductCreateStore } from "../../store/useProductCreateStore";
import { removeVietnameseTones } from "../../_base/helper/FunctionHelper";

const TinyMCEEditor = ({
  contentText,
  height,
  nameOnchange,
  setValue,
  addImageToArticle,
}: {
  contentText: string;
  height?: string;
  nameOnchange?: string;
  setValue: (value: any) => void;
  addImageToArticle?: string;
}) => {
  const setDataItemBase = useProductCreateStore(
    (state) => state.setDataItemBase
  );
  const dataItemBase = useProductCreateStore((state) => state.dataItemBase);
  const [visible, { toggle, open, close }] = useDisclosure(false);
  const editorRef = useRef<any>(null);
  const editorInstanceRef = useRef<any>(null); // Stable reference for the actual TinyMCE instance
  const editorReadyRef = useRef<boolean>(false); // Track if editor has fully initialized
  const containerRef = useRef<HTMLDivElement>(null);

  const handleEditorChange = useDebouncedCallback((content: string) => {
    setValue(content);
  }, 300);

  const insertImageAtCursor = useCallback((imageUrl: string) => {
    const editor = editorInstanceRef.current;
    if (!editor) {
      console.error("Editor instance is not available");
      return;
    }

    // Kiểm tra xem editor có đang focus không
    if (!editor.hasFocus()) {
      console.warn("Editor is not focused. Focusing editor first.");
      editor.focus();
    }

    // Chèn ảnh vào vị trí con trỏ hiện tại
    editor.execCommand(
      "mceInsertContent",
      false,
      `<img src="${imageUrl}" alt="${removeVietnameseTones(
        String(imageUrl)
          .substring(imageUrl.lastIndexOf("/") + 1, imageUrl.lastIndexOf("."))
          .toLowerCase()
      )}" />`
    );
  }, []);

  useEffect(() => {
    if (addImageToArticle && addImageToArticle.length > 0) {
      insertImageAtCursor(addImageToArticle);
    }
  }, [addImageToArticle, insertImageAtCursor]);

  const handleImageUpload = useCallback(async (
    image: File,
    success: (url: string) => void,
    failure: (message: string) => void
  ) => {
    try {
      open();
      const response = await uploadFile([image]);
      console.log("response", response);

      if (response && response.success && response.data) {
        console.log("Upload response:", response);

        // Lấy URL từ response
        let url = response.data[0]?.url;
        console.log("Original URL:", url);

        if (typeof url === "string") {
          // Convert link nếu cần
          if (url.startsWith("http://192.168.2.17:9500/")) {
            url = url.replace(
              "http://192.168.2.17:9500/",
              "https://image-dev.hacom.vn/"
            );
          }

          console.log("Final URL for TinyMCE:", url);
          success(url);
        } else {
          console.error("URL is not a string:", url);
          throw new Error("Invalid URL format returned from upload");
        }
      } else {
        console.error("Upload failed:", response);
        throw new Error("Failed to upload image - no response data");
      }
    } catch (error) {
      failure("An error occurred during image upload");
    } finally {
      close();
    }
  }, [open, close]);

  // Xử lý \r\n hoặc \n thành <br> nếu chưa có HTML tag
  let formattedContent = contentText?.includes("<")
    ? contentText // Nếu đã là HTML thì giữ nguyên
    : (contentText || "").replace(/\r\n|\n/g, "<br>");

  // Nếu có thẻ <img, convert domain ảnh
  if (formattedContent?.includes("<img")) {
    formattedContent = formattedContent.replace(
      /<img([^>]+)src="([^"]+)"([^>]*)>/g,
      (match, p1, src, p2) => `<img${p1}src="${convertImageDomain(src)}"${p2}>`
    );
  }

  // Khởi tạo giá trị ban đầu tĩnh cho editor một lần duy nhất khi component mount.
  // Điều này ngăn chặn việc @tinymce/tinymce-react tự động reset content khi props thay đổi.
  const [initialEditorContent] = useState(() => formattedContent);

  // Đồng bộ hóa content từ bên ngoài (ví dụ khi API trả về kết quả chậm hoặc auto-fill)
  // Chỉ chạy khi editorReadyRef.current là true để tránh lỗi 'select' of undefined.
  // Đồng thời KHÔNG chạy khi editor đang được focus để tránh lỗi nhảy con trỏ chuột khi người dùng đang nhập.
  useEffect(() => {
    if (editorInstanceRef.current && editorReadyRef.current) {
      const editor = editorInstanceRef.current;
      if (!editor.hasFocus()) {
        const currentContent = editor.getContent();
        if (formattedContent !== currentContent) {
          editor.setContent(formattedContent);
        }
      }
    }
  }, [formattedContent]);

  // Memoize cấu hình init để tránh re-initialize editor khi component re-render
  const initConfig = useMemo(() => {
    return {
      height: height ? Number(height) : 500,
      width: "100%",
      menubar: true,
      resize: true,
      statusbar: true,
      branding: false,
      elementpath: false,
      min_height: 200,
      relative_urls: false,
      remove_script_host: false,
      convert_urls: false,
      setup: (editor: any) => {
        // Lưu reference đến editor
        editorInstanceRef.current = editor;

        editor.on("init", () => {
          editorReadyRef.current = true;
          // Đảm bảo resize handle hiển thị
          const container = editor.getContainer();
          if (container) {
            container.style.resize = "vertical"; // Bật lại resize cho TinyMCE
            container.style.overflow = "hidden";
          }
        });

        // Theo dõi sự kiện resize của editor
        editor.on("ResizeEditor", () => {
          setTimeout(() => {
            if (containerRef.current) {
              const editorContainer = editor.getContainer();
              if (editorContainer) {
                const editorHeight = editorContainer.offsetHeight;
                const newContainerHeight = editorHeight + 20; // Thêm padding
                containerRef.current.style.height = `${newContainerHeight}px`;
              }
            }
          }, 10); // Delay nhỏ để đảm bảo TinyMCE đã hoàn thành resize
        });
      },
      plugins: [
        "advlist",
        "autolink",
        "lists",
        "link",
        "image",
        "charmap",
        "print",
        "preview",
        "anchor",
        "searchreplace",
        "visualblocks",
        "code",
        "fullscreen",
        "insertdatetime",
        "media",
        "table",
        "paste",
        "help",
        "wordcount",
      ],
      toolbar:
        "undo redo | blocks | formatselect | bold italic backcolor | link | " +
        "alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | " +
        "removeformat | image | media | table | wordcount | code | help",
      image_title: true,
      automatic_uploads: true,
      file_picker_types: "image",
      auto_list: false,
      file_picker_callback: (callback: any, value: any, meta: any) => {
        if (meta.filetype === "image") {
          const input = document.createElement("input");
          input.setAttribute("type", "file");
          input.setAttribute("accept", "image/*");
          input.addEventListener("change", function (this: HTMLInputElement) {
            const fileList = this.files;
            if (fileList && fileList.length > 0) {
              const file = fileList[0];
              handleImageUpload(
                file,
                (url) =>
                  callback(url, {
                    alt: removeVietnameseTones(
                      String(url)
                        .substring(
                          url.lastIndexOf("/") + 1,
                          url.lastIndexOf(".")
                        )
                        .toLowerCase()
                    ),
                  }),
                (message) => console.error(message)
              );
            }
          });
          input.click();
        }
      },
    };
  }, [height, handleImageUpload]);

  return (
    <Box>
      <LoadingOverlay
        visible={visible}
        zIndex={9000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />
      <div
        ref={containerRef}
        style={{
          resize: "none", // Tắt resize của container vì ta sẽ dùng TinyMCE resize
          overflow: "hidden",
          border: "1px solid #ccc",
          minHeight: "200px",
          minWidth: "300px",
        }}
      >
        <Editor
          ref={editorRef}
          apiKey="wwheiwaj492tbs9774t0z89v05r3xt8eg1ljilyc5d784xsv"
          initialValue={initialEditorContent}
          init={initConfig}
          onEditorChange={(e) => {
            handleEditorChange(e);
          }}
        />
      </div>
    </Box>
  );
};

export default TinyMCEEditor;
